import campaignsApi from 'common/src/modules/campaigns/campaigns.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {sharedCampaigns} from '@cl/shared';
import {ConditionCategory} from 'common/src/modules/conditions/conditions.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {DTProductSelectionNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {ProductType} from 'common/src/modules/products/products.enums';
import {ConditionOperatorKeyToText} from 'common/src/modules/conditions/conditions.types';
import {ConditionOperatorKey} from 'common/src/modules/conditions/conditions.types';
import {conditionCategoryData} from 'common/src/modules/conditions/conditions.consts';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {AIConditionSignal} from '../../promptToCardAgentV2.types';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';

const searchSignalToolSchema = z.object({
  conditionField: z
    .string()
    .describe(
      '1-3 word signal search term (from placement data, DSP data, user data, or previously selected products/locations/content) by which an output will be selected',
    ),
});

export default function searchSignalTool(store: CLStore) {
  async function execute({
    input: {conditionField},
  }: ToolExecuteParams<typeof searchSignalToolSchema>): Promise<ToolResult> {
    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    if (!campaign) {
      return toolResult('Campaign is required to get condition signals');
    }

    if (!versionId) {
      return toolResult('Version ID is required to get condition signals');
    }

    const signals = await campaignsApi.getSignalsByNameForConditionCard(
      conditionField,
      campaign.id,
      versionId,
    );

    // Filter signals based on feeds in the campaign
    const nodes = sharedCampaigns.nodesRef.value;
    const requiredCategories: ConditionCategory[] = [];

    for (const node of Object.values(nodes)) {
      if (node.type === NodeType.PRODUCTS) {
        const productNode = node as DTProductSelectionNode;

        if (productNode.feed?.productType !== undefined) {
          const productType = productNode.feed.productType;

          if (productType === ProductType.Ecommerce) {
            requiredCategories.push(ConditionCategory.Ecommerce);
          } else if (productType === ProductType.Automotive) {
            requiredCategories.push(ConditionCategory.Car);
          } else if (productType === ProductType.Flight) {
            requiredCategories.push(ConditionCategory.Flight);
          }
        }
      }
    }

    let filteredSignals = signals;

    if (requiredCategories.length > 0) {
      filteredSignals = signals.filter((signal) => {
        return signal.categories.some((category) => requiredCategories.includes(category));
      });

      // If no signals remain after filtering, return the original list
      if (filteredSignals.length === 0) {
        filteredSignals = signals;
      }
    }

    const signalsMetadata: AIConditionSignal[] = filteredSignals.map((signal) => ({
      signalKey: signal.value,
      signalName: signal.name,
      categories: signal.categories.map((category) => {
        const categoryData = conditionCategoryData[category as ConditionCategory];

        return {
          value: categoryData.value,
          title: categoryData.title,
        };
      }),
      signalType: signal.type as MacroItemTypeValue,
      optionalOperators: (signal.optionalOperators || []).map((operator: ConditionOperatorKey) => {
        return {
          value: operator,
          title: ConditionOperatorKeyToText[operator],
        };
      }),
      shouldQueryOptionalValues: signal.shouldQueryOptionalValues || false,
    }));

    if (signalsMetadata.length === 0) return toolResult('No signals found');

    return toolResult(`Signals found: ${JSON.stringify(signalsMetadata)}`);
  }

  return tool({
    name: V2CardAgentToolName.SearchSignal,
    displayName: 'Searching signal',
    description: 'Search for a signal',
    execute,
    inputSchema: searchSignalToolSchema,
  });
}
